<template>
  <el-dialog
    :model-value="visible"
    title="🔁 批量添加重复任务"
    width="760px"
    :close-on-click-modal="false"
    @update:model-value="close"
  >
    <div class="brd-body">
      <!-- 1. 日期区间 -->
      <section class="brd-section">
        <div class="brd-label">日期区间</div>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          unlink-panels
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="YYYY-MM-DD"
          style="width: 100%"
        />
      </section>

      <!-- 2. 重复规则 -->
      <section class="brd-section">
        <div class="brd-label">重复规则</div>
        <el-radio-group v-model="form.repeatMode">
          <el-radio-button label="DAILY">每天</el-radio-button>
          <el-radio-button label="WORKDAY">仅工作日</el-radio-button>
          <el-radio-button label="WEEKLY">指定星期</el-radio-button>
        </el-radio-group>

        <div v-if="form.repeatMode === 'WEEKLY'" class="brd-sub">
          <el-checkbox-group v-model="form.weekdays">
            <el-checkbox-button
              v-for="w in weekdayOptions"
              :key="w.value"
              :label="w.value"
            >{{ w.label }}</el-checkbox-button>
          </el-checkbox-group>
        </div>

        <div v-if="form.repeatMode === 'DAILY'" class="brd-sub">
          <el-checkbox v-model="form.skipSunday">跳过周日</el-checkbox>
        </div>
      </section>

      <!-- 3. 截止时间 -->
      <section class="brd-section">
        <div class="brd-label">截止时间</div>
        <el-radio-group v-model="form.deadlineMode">
          <el-radio label="SAME_DAY">当天 23:59（推荐）</el-radio>
          <el-radio label="RANGE_END">统一为区间结束日</el-radio>
        </el-radio-group>
      </section>

      <!-- 4. 任务模板 -->
      <section class="brd-section">
        <div class="brd-label-row">
          <span class="brd-label">
            每天要做的工作
            <span class="brd-hint">（每个条目会在每个命中日期各生成一条任务）</span>
          </span>
          <el-button size="small" type="primary" plain @click="addTemplate">
            + 添加一项
          </el-button>
        </div>

        <div class="brd-tpl-head">
          <span class="c-name">任务名称</span>
          <span class="c-hours">工时</span>
          <span class="c-type">类型</span>
          <span class="c-imp">重要程度</span>
          <span class="c-op"></span>
        </div>

        <div v-for="(tpl, i) in form.templates" :key="i" class="brd-tpl-row">
          <el-input
            v-model="tpl.taskName"
            class="c-name"
            placeholder="例如：每日站会"
            maxlength="50"
          />
          <el-input-number
            v-model="tpl.requiredHours"
            class="c-hours"
            :min="0.5"
            :max="24"
            :step="0.5"
            :controls="false"
          />
          <el-select v-model="tpl.taskType" class="c-type" placeholder="类型">
            <el-option
              v-for="t in taskTypeOptions"
              :key="t"
              :label="t"
              :value="t"
            />
          </el-select>
          <el-select v-model="tpl.importance" class="c-imp" placeholder="重要程度">
            <el-option label="正常" value="正常" />
            <el-option label="重要" value="重要" />
            <el-option label="非常重要" value="非常重要" />
          </el-select>
          <el-button
            class="c-op"
            size="small"
            type="danger"
            plain
            :disabled="form.templates.length === 1"
            @click="removeTemplate(i)"
          >删除</el-button>
        </div>
      </section>

      <!-- 5. 试算预览 -->
      <section class="brd-preview" :class="{ 'is-error': previewError }">
        <template v-if="previewError">
          <span class="p-icon">⚠️</span>
          <span class="p-text">{{ previewError }}</span>
        </template>
        <template v-else-if="preview">
          <span class="p-icon">🧮</span>
          <span class="p-text">
            命中 <b>{{ preview.dayCount }}</b> 天 ×
            <b>{{ preview.templateCount }}</b> 项工作 =
            将创建 <b class="p-strong">{{ preview.willCreate }}</b> 条任务
          </span>
          <span v-if="preview.dates && preview.dates.length" class="p-dates">
            首个 {{ preview.dates[0] }}，末个 {{ preview.dates[preview.dates.length - 1] }}
          </span>
        </template>
        <template v-else>
          <span class="p-icon">🧮</span>
          <span class="p-text p-muted">选好区间和规则后这里会显示将创建的任务数</span>
        </template>
      </section>
    </div>

    <template #footer>
      <el-button @click="close">取消</el-button>
      <el-button
        type="primary"
        :loading="submitting"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ submitting ? '创建中...' : '确认创建' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import * as taskApi from '../api/task'

export default {
  name: 'BatchRepeatDialog',
  props: {
    visible: { type: Boolean, default: false }
  },
  emits: ['update:visible', 'created'],
  setup(props, { emit }) {
    const taskTypeOptions = ['程序开发', '文章撰写', '程序修改', '文章修改', '报告编写']
    const weekdayOptions = [
      { label: '周一', value: 1 },
      { label: '周二', value: 2 },
      { label: '周三', value: 3 },
      { label: '周四', value: 4 },
      { label: '周五', value: 5 },
      { label: '周六', value: 6 },
      { label: '周日', value: 7 }
    ]

    const makeTemplate = () => ({
      taskName: '',
      requiredHours: 1,
      taskType: '程序开发',
      importance: '正常'
    })

    const dateRange = ref([])
    const submitting = ref(false)
    const preview = ref(null)
    const previewError = ref('')

    const form = reactive({
      repeatMode: 'DAILY',
      weekdays: [1, 2, 3, 4, 5],
      skipSunday: false,
      deadlineMode: 'SAME_DAY',
      templates: [makeTemplate()]
    })

    const addTemplate = () => form.templates.push(makeTemplate())
    const removeTemplate = (i) => form.templates.splice(i, 1)

    const buildPayload = () => ({
      startDate: dateRange.value && dateRange.value[0],
      endDate: dateRange.value && dateRange.value[1],
      repeatMode: form.repeatMode,
      weekdays: form.weekdays,
      skipSunday: form.skipSunday,
      deadlineMode: form.deadlineMode,
      templates: form.templates.map(t => ({
        taskName: (t.taskName || '').trim(),
        requiredHours: t.requiredHours,
        taskType: t.taskType,
        importance: t.importance
      }))
    })

    const hasValidNames = computed(
      () => form.templates.every(t => (t.taskName || '').trim().length > 0)
    )

    const canSubmit = computed(() =>
      !!(dateRange.value && dateRange.value.length === 2) &&
      hasValidNames.value &&
      !!preview.value &&
      preview.value.willCreate > 0 &&
      !previewError.value
    )

    // 试算（防抖），规则一变就重新算
    let debounceTimer = null
    const runPreview = () => {
      if (!dateRange.value || dateRange.value.length !== 2) {
        preview.value = null
        previewError.value = ''
        return
      }
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(async () => {
        try {
          const res = await taskApi.previewBatchRepeat(buildPayload())
          if (res.data.code === 200) {
            preview.value = res.data.data
            previewError.value = ''
          } else {
            preview.value = null
            previewError.value = res.data.message || '试算失败'
          }
        } catch (e) {
          preview.value = null
          previewError.value = '试算失败，请检查网络'
        }
      }, 300)
    }

    watch(
      () => [dateRange.value, form.repeatMode, form.weekdays, form.skipSunday, form.templates.length],
      runPreview,
      { deep: true }
    )

    const reset = () => {
      dateRange.value = []
      form.repeatMode = 'DAILY'
      form.weekdays = [1, 2, 3, 4, 5]
      form.skipSunday = false
      form.deadlineMode = 'SAME_DAY'
      form.templates = [makeTemplate()]
      preview.value = null
      previewError.value = ''
    }

    const close = () => {
      emit('update:visible', false)
    }

    // 关闭后清空，下次打开是干净状态
    watch(() => props.visible, (v) => {
      if (!v) reset()
    })

    const submit = async () => {
      if (!canSubmit.value) return
      submitting.value = true
      try {
        const res = await taskApi.batchAddRepeatTasks(buildPayload())
        if (res.data.code === 200) {
          const d = res.data.data
          ElMessage.success(`已创建 ${d.created} 条任务，覆盖 ${d.dayCount} 天`)
          emit('created', d)
          close()
        } else {
          ElMessage.error(res.data.message || '批量添加失败')
        }
      } catch (e) {
        ElMessage.error('批量添加失败，请稍后重试')
      } finally {
        submitting.value = false
      }
    }

    return {
      taskTypeOptions,
      weekdayOptions,
      dateRange,
      form,
      preview,
      previewError,
      submitting,
      canSubmit,
      addTemplate,
      removeTemplate,
      submit,
      close
    }
  }
}
</script>

<style scoped>
.brd-body {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 4px;
}

.brd-section {
  margin-bottom: 22px;
}

.brd-label {
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 10px;
  display: block;
}

.brd-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.brd-label-row .brd-label {
  margin-bottom: 0;
}

.brd-hint {
  font-size: 12px;
  font-weight: 400;
  color: #86868b;
}

.brd-sub {
  margin-top: 12px;
}

/* 模板表格 */
.brd-tpl-head,
.brd-tpl-row {
  display: grid;
  grid-template-columns: 1fr 90px 130px 120px 64px;
  gap: 8px;
  align-items: center;
}

.brd-tpl-head {
  font-size: 12px;
  color: #86868b;
  padding: 0 2px 6px;
}

.brd-tpl-row {
  margin-bottom: 8px;
}

.brd-tpl-row .c-hours {
  width: 100%;
}

/* 预览条 */
.brd-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  background: #F5F7FA;
  border: 1px solid #E4E7ED;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 13px;
  color: #1d1d1f;
}

.brd-preview.is-error {
  background: #FEF0F0;
  border-color: #FDE2E2;
  color: #F56C6C;
}

.p-strong {
  color: #007AFF;
  font-size: 15px;
}

.p-muted {
  color: #86868b;
}

.p-dates {
  color: #86868b;
  font-size: 12px;
}

@media (max-width: 768px) {
  .brd-tpl-head {
    display: none;
  }

  .brd-tpl-row {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
